import { camelizeKeys, decamelizeKeys } from 'humps';
import Cookies from 'js-cookie';
import { CONSENT_TYPE, COOKIE_PURPOSE_LIMITATION_NAMES, SERVER_PURPOSE_LIMITATION_NAMES, NI_C_COOKIES, SQ_COOKIES, } from '@nike/privacy-shared-logic';
import getPurposeLimitationsMemberState from '../store/selectors/getPurposeLimitationsMemberState';
import getPurposeLimitationsGuestState from '../store/selectors/getPurposeLimitationsGuestState';
import { COOKIE_PURPOSE_LIMITATIONS_EXPIRATION_PERIOD, SQ_COOKIE_EXPIRATION_TIME, ROUTES_WITH_EXTRA_PRIVACY_PARAM, THIRTEEN_MONTHS_IN_MILLISECONDS, THIRTEEN_MONTHS_RE_PROMPT_COUNTRIES, countriesWhereSqCookieIsDeprecated, allowedCountriesWithNikePrivacy, } from '../constants';
import { resolveConsentCookieDomain } from '../helpers/resolveConsentCookieDomain';
import * as API from '../API';
import { store } from '../store';
export const getPurposeLimitationByName = (purposeLimitations, name) => purposeLimitations.find(({ category }) => category === name);
export const getLevelsFromLimitations = (purposeLimitations) => purposeLimitations.reduce((accumulator, currentValue) => ({
    ...accumulator,
    [currentValue.category]: currentValue.level,
}), {});
const isStoredMoreThan13Month = (lastUpdatedTimestamp) => Date.now() - lastUpdatedTimestamp > THIRTEEN_MONTHS_IN_MILLISECONDS;
export const checkIs13MonthRePromptNeeded = (lastUpdatedTimestamp, location) => {
    if (!THIRTEEN_MONTHS_RE_PROMPT_COUNTRIES.includes(location) || !lastUpdatedTimestamp)
        return false;
    return isStoredMoreThan13Month(lastUpdatedTimestamp);
};
const getAllowPerformance = (personalizationPermission, performancePermission) => {
    if (!personalizationPermission && (performancePermission === null || performancePermission === void 0 ? void 0 : performancePermission.isEnabled))
        return true;
    if ((personalizationPermission === null || personalizationPermission === void 0 ? void 0 : personalizationPermission.isEnabled) && (performancePermission === null || performancePermission === void 0 ? void 0 : performancePermission.isEnabled))
        return true;
    return false;
};
export const getSqCookieValueToSet = (purposeLimitations) => {
    const personalizationPermission = getPurposeLimitationByName(purposeLimitations, SERVER_PURPOSE_LIMITATION_NAMES.PERSONALIZATION_TARGETED_COMMS);
    const behavioralPermission = getPurposeLimitationByName(purposeLimitations, SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_BEHAVIORAL_EVENTS);
    const performancePermission = getPurposeLimitationByName(purposeLimitations, SERVER_PURPOSE_LIMITATION_NAMES.PERFORMANCE);
    const allowMarketing = !!(behavioralPermission === null || behavioralPermission === void 0 ? void 0 : behavioralPermission.isEnabled);
    const allowPerformance = getAllowPerformance(personalizationPermission, performancePermission);
    let level;
    if (!allowMarketing && !allowPerformance)
        level = 0;
    if (!allowMarketing && allowPerformance)
        level = 1;
    if (allowMarketing && !allowPerformance)
        level = 2;
    if (allowMarketing && allowPerformance)
        level = 3;
    return level;
};
export const setSqCookie = (purposeLimitations) => {
    Cookies.remove(SQ_COOKIES, {
        path: '/',
        domain: process.env.HOST_NAME,
    });
    const sqCookieValueToSet = getSqCookieValueToSet(purposeLimitations);
    sqCookieValueToSet !== undefined &&
        Cookies.set('sq', `${sqCookieValueToSet}`, {
            expires: SQ_COOKIE_EXPIRATION_TIME,
            path: '/',
            domain: resolveConsentCookieDomain(),
        });
};
export const checkIsSqCookieDeprecatedByLocation = (location) => {
    if (!location)
        return false;
    return (allowedCountriesWithNikePrivacy.includes(location) &&
        countriesWhereSqCookieIsDeprecated.includes(location));
};
const removeLevelField = (limitation) => {
    const limitationCopy = JSON.parse(JSON.stringify(limitation));
    delete limitationCopy.level;
    return {
        ...limitationCopy,
        userLicenses: limitationCopy.userLicenses.map(userLicense => {
            const userLicenseCopy = { ...userLicense };
            delete userLicenseCopy.level;
            return userLicenseCopy;
        }),
    };
};
const transformLimitationConsent = (consent) => {
    const limitationsValue = {
        isEnabled: false,
        isDefault: false,
    };
    switch (consent) {
        case CONSENT_TYPE.ACCEPT: {
            limitationsValue.isEnabled = true;
            limitationsValue.isDefault = false;
            break;
        }
        case CONSENT_TYPE.DECLINE: {
            limitationsValue.isEnabled = false;
            limitationsValue.isDefault = false;
            break;
        }
        case CONSENT_TYPE.DEFAULT_ACCEPT: {
            limitationsValue.isEnabled = true;
            limitationsValue.isDefault = true;
            break;
        }
        case CONSENT_TYPE.DEFAULT_DECLINE: {
            limitationsValue.isEnabled = false;
            limitationsValue.isDefault = true;
            break;
        }
    }
    return limitationsValue;
};
const convertSelectedLimitationsToOutput = (consent) => consent
    ? {
        isEnabled: true,
        isDefault: false,
    }
    : {
        isEnabled: false,
        isDefault: false,
    };
export const transformConsent = (limitation) => {
    const limitationCopy = JSON.parse(JSON.stringify(limitation));
    const consentValue = limitationCopy.consent;
    delete limitationCopy.consent;
    const limitationsValue = transformLimitationConsent(consentValue);
    limitationCopy.isEnabled = limitationsValue.isEnabled;
    limitationCopy.isDefault = limitationsValue.isDefault;
    return limitationCopy;
};
const transformConsentField = (limitation) => {
    const limitationCopy = JSON.parse(JSON.stringify(limitation));
    const transformedInitialLayer = transformConsent(limitationCopy);
    return {
        ...transformedInitialLayer,
        userLicenses: transformedInitialLayer.userLicenses.map(userLicense => transformConsent(userLicense)),
    };
};
export const transformLimitation = (limitation) => {
    const limitationWithoutLevel = removeLevelField(limitation);
    return transformConsentField(limitationWithoutLevel);
};
export const transformLimitations = (purposeLimitations) => purposeLimitations.map(purposeLimitation => {
    const limitationWithoutLevel = removeLevelField(purposeLimitation);
    return transformConsentField(limitationWithoutLevel);
});
export const fetchPurposeLimitationsMember = async ({ accessToken, userExperience, memberLocation, noCache, }) => {
    const result = await API.getPurposeLimitationsMember({
        accessToken,
        userExperience,
        memberLocation,
        noCache,
    });
    if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
    }
    const purposeLimitations = await result.json();
    return camelizeKeys(purposeLimitations);
};
export const fetchPurposeLimitationsGuest = async (userExperience, guestLocation) => {
    if (!guestLocation) {
        throw new Error('Location is required to get Purpose Limitations');
    }
    const result = await API.getPurposeLimitationsGuest({
        userExperience,
        guestLocation,
    });
    if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
    }
    const purposeLimitations = await result.json();
    return camelizeKeys(purposeLimitations);
};
const getOriginApplicationFromPath = () => {
    const pathParams = window.location.pathname.split('/');
    return pathParams.find((_, index) => {
        return ROUTES_WITH_EXTRA_PRIVACY_PARAM.includes(pathParams[index - 1]);
    });
};
const getPurposeLimitationsBodyUpdate = (props, language) => {
    const originApplication = getOriginApplicationFromPath();
    return decamelizeKeys({
        ...props,
        ...(originApplication && { originApplication }),
        language,
        selectionTimestamp: new Date().toISOString(),
        consentSelections: props.consentSelections.map(consentSelection => ({
            ...consentSelection,
            consent: consentSelection.consent === true ? CONSENT_TYPE.ACCEPT : CONSENT_TYPE.DECLINE,
        })),
    });
};
export const updatePurposeLimitationsMember = async ({ contextFrameToUpdate, accessToken, language, userExperience, memberLocation, }) => {
    if (!accessToken ||
        !contextFrameToUpdate.contextFrameId ||
        !contextFrameToUpdate.contextFrameVersionId) {
        throw new Error('Incorrect Context Frames data provided to update Purpose limitations');
    }
    const { contextFrameId, ...restContextFrameToUpdate } = contextFrameToUpdate;
    const contextFrameToUpdateBody = getPurposeLimitationsBodyUpdate(restContextFrameToUpdate, language);
    const result = await API.updatePurposeLimitations({
        accessToken,
        contextFrameToUpdateBody,
        contextFrameId,
        userExperience,
        memberLocation,
    });
    if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
    }
    const purposeLimitations = await result.json();
    return camelizeKeys(purposeLimitations);
};
export const transformConsentsFromBooleanValue = (consentSelections) => consentSelections.reduce((accumulator, currentValue) => ({
    ...accumulator,
    [currentValue.purposeLimitationCategory]: convertSelectedLimitationsToOutput(currentValue.consent),
}), {});
export const transformGuestPurposeLimitations = (purposeLimitations, consentSelectionAttachedToCategory) => purposeLimitations.map((purposeLimitation) => {
    if (typeof consentSelectionAttachedToCategory[purposeLimitation.category] === 'undefined') {
        return purposeLimitation;
    }
    const purposeLimitationCopy = JSON.parse(JSON.stringify(purposeLimitation));
    const { isEnabled, isDefault } = consentSelectionAttachedToCategory[purposeLimitation.category];
    purposeLimitationCopy.isEnabled = isEnabled;
    purposeLimitationCopy.isDefault = isDefault;
    purposeLimitationCopy.userLicenses.forEach((userLicense) => {
        userLicense.isEnabled = isEnabled;
        userLicense.isDefault = isDefault;
    });
    return purposeLimitationCopy;
});
export const updatePurposeLimitationsGuest = (contextFrameToUpdate) => {
    if (!contextFrameToUpdate.contextFrameId || !contextFrameToUpdate.contextFrameVersionId) {
        throw new Error('Incorrect Context Frames data provided to update Purpose limitations');
    }
    const { consentSelections } = contextFrameToUpdate;
    const { purposeLimitations } = getPurposeLimitationsGuestState(store.getState());
    return transformGuestPurposeLimitations(purposeLimitations, transformConsentsFromBooleanValue(consentSelections));
};
export const checkRetrievingLimitationMemberFromStore = () => {
    const { purposeLimitations, error } = getPurposeLimitationsMemberState(store.getState());
    return !(error || !purposeLimitations || !purposeLimitations.length);
};
export const checkRetrievingLimitationGuestFromStore = () => {
    const { purposeLimitations, error } = getPurposeLimitationsGuestState(store.getState());
    return !(error || !purposeLimitations || !purposeLimitations.length);
};
export const getConsentSelectionsForCookie = (purposeLimitation) => {
    return purposeLimitation.isEnabled === true ? '1' : '0';
};
export const getCookiesPurposeLimitationsEnumKeys = (enumKey) => {
    return COOKIE_PURPOSE_LIMITATION_NAMES[enumKey];
};
export const getServerPurposeLimitationEnumKey = (purposeLimitation) => {
    const purposeLimitationEnumKeys = Object.keys(SERVER_PURPOSE_LIMITATION_NAMES);
    const purposeLimitationsEnumValues = Object.values(SERVER_PURPOSE_LIMITATION_NAMES);
    const indexOfPurposeLimitationEnumValue = purposeLimitationsEnumValues.indexOf(purposeLimitation.category);
    return indexOfPurposeLimitationEnumValue >= 0
        ? purposeLimitationEnumKeys[indexOfPurposeLimitationEnumValue]
        : undefined;
};
export const getCookiePurposeLimitationsValuesToSet = (purposeLimitations) => {
    const cookieEnumKeysWithConsents = purposeLimitations.map(purposeLimitation => {
        const serverPurposeLimitationEnumKey = getServerPurposeLimitationEnumKey(purposeLimitation);
        if (typeof serverPurposeLimitationEnumKey === 'undefined') {
            return;
        }
        const cookieEnumKey = getCookiesPurposeLimitationsEnumKeys(serverPurposeLimitationEnumKey);
        if (typeof cookieEnumKey === 'undefined') {
            return;
        }
        const cookieConsent = getConsentSelectionsForCookie(purposeLimitation);
        return `${cookieEnumKey}=${cookieConsent}`;
    });
    return cookieEnumKeysWithConsents
        .sort()
        .filter(cookieEnumKeyWithConsent => typeof cookieEnumKeyWithConsent !== 'undefined')
        .join('|');
};
export const setNiCCookie = (purposeLimitations) => {
    Cookies.remove(NI_C_COOKIES, {
        path: '/',
        domain: process.env.HOST_NAME,
    });
    const cookiePurposeLimitationsValuesToSet = getCookiePurposeLimitationsValuesToSet(purposeLimitations);
    Cookies.set(NI_C_COOKIES, cookiePurposeLimitationsValuesToSet, {
        expires: COOKIE_PURPOSE_LIMITATIONS_EXPIRATION_PERIOD,
        path: '/',
        domain: resolveConsentCookieDomain(),
    });
};
const getMainUrl = (currentDomainContainsWww) => {
    const subdomainLink = Cookies.get('nikePrivacySubDomainLink');
    if (subdomainLink) {
        return subdomainLink;
    }
    return currentDomainContainsWww ? 'www.nike.com' : 'nike.com';
};
export const checkIsSubDomain = () => {
    const currentDomain = window.location.hostname;
    const currentDomainContainsWww = currentDomain.includes('www.');
    const baseDomain = getMainUrl(currentDomainContainsWww);
    const currentDomainParts = currentDomain.split('.');
    const baseDomainParts = baseDomain.split('.');
    if (currentDomainParts.length <= baseDomainParts.length)
        return false;
    return currentDomain.endsWith(`.${baseDomain.replace('www.', '')}`);
};
